import React, { useState, useRef, useEffect } from 'react';
import { usePermissions, NavigationMenuType } from "../firebase/PermissionProvider";
import {
  AppBar,
  Avatar,
  Drawer,
  Toolbar,
  Typography,
  Divider,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuList,
  MenuItem,
  Fade,
  Backdrop,
  Paper,
  ListSubheader,
} from '@mui/material';
import * as Icons from "../constants/icons";
import {
  grey,
  green,
} from '@mui/material/colors';
import { MENU_CATEGORIES } from '../constants/masterData';
import { getAuth, signOut } from "firebase/auth";
import { useAuth } from "../firebase/AuthProvider";
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { isExternalMenuPath } from '../utils/menuLink';

// 子メニュー表示用のステート型
interface ActiveSubmenuType {
  menuId: string;
  children: NavigationMenuType[];
  top: number;
}

export default function NavigationBar() {
  const { user } = useAuth();
  const { permissionRolesData, permissionData } = usePermissions();
  const [slideOpen, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const openClientMenu = Boolean(anchorEl);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const submenuSwitchTimerRef = useRef<NodeJS.Timeout | null>(null);
  // 親 Drawer の Paper 要素 (persistent のため閉じてもスクロール位置が残る)
  const drawerPaperRef = useRef<HTMLDivElement | null>(null);

  // サイドバーが非表示になったらスクロール位置をトップへ戻す
  useEffect(() => {
    if (!slideOpen && drawerPaperRef.current) {
      drawerPaperRef.current.scrollTop = 0;
    }
  }, [slideOpen]);

  // カスケード方式: 子メニュー用のステート
  const [activeSubmenu, setActiveSubmenu] = useState<ActiveSubmenuType | null>(null);

  // タイマー停止
  const stopCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  // 子メニュー切り替えタイマー停止
  const stopSubmenuSwitchTimer = () => {
    if (submenuSwitchTimerRef.current) {
      clearTimeout(submenuSwitchTimerRef.current);
      submenuSwitchTimerRef.current = null;
    }
  };

  // タイマー開始 (一定時間後に閉じる)
  const startCloseTimer = () => {
    stopCloseTimer();
    stopSubmenuSwitchTimer();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      setActiveSubmenu(null);
    }, 400);
  };

  // 子メニューを開く (位置計算付き)
  const handleSubMenuOpen = (menuId: string, event: React.MouseEvent<HTMLElement>, children: NavigationMenuType[]) => {
    stopCloseTimer(); // 移動中もタイマーを止める
    const rect = event.currentTarget.getBoundingClientRect();
    setActiveSubmenu({
      menuId,
      children,
      top: rect.top
    });
  };

  // アカウントメニュー
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = () => {
    stopCloseTimer();
    stopSubmenuSwitchTimer();
    setTimeout(() => {
      setOpen(prev => !prev);
      if (slideOpen) {
        setActiveSubmenu(null);
      }
    }, 50);
  };

  // ヘッダーのメニューアイコン用 (ホバー・クリックとも常に「開く」)
  // ※ トグルにすると「ホバーで開く→クリックで閉じる」「タッチ端末で mouseenter+click が
  //    連続発火して開かない」となるため、閉じる操作は Drawer 側のボタン / Backdrop / マウス離脱に委ねる
  const handleOpen = () => {
    stopCloseTimer();
    stopSubmenuSwitchTimer();
    setOpen(true);
  };



  ///////////////////////////////////////////////////////////////////////////////
  // IconRenderer 利用アイコンを返す
  ///////////////////////////////////////////////////////////////////////////////
  const IconRenderer = ({ iconName }: { iconName: string }) => {
    const SelectedIcon = Icons[iconName as keyof typeof Icons];
    if (!SelectedIcon) {
      return <Icons.HelpOutline />;
    }
    return <SelectedIcon />;
  };

  ///////////////////////////////////////////////////////////////////////////////
  // logout 処理
  ///////////////////////////////////////////////////////////////////////////////
  const navigate = useNavigate();

  const handleLogout = async () => {
    const auth = getAuth();
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("ログアウトエラー:", error);
    }
  };

  // -------------------------------------------------------------
  // Header Menu
  // -------------------------------------------------------------
  const HeaderMenuList = (
    <Toolbar
      sx={{
        minHeight: '30px !important',
        backgroundColor: 'transparent',
        boxShadow: 'none',
        pointerEvents: 'none', // 透明な帯が背後の要素のクリックを奪わないようにする
      }}
    >
      <IconButton
        edge="start"
        onClick={handleOpen}
        onMouseEnter={handleOpen}
        sx={{ color: grey[500], pointerEvents: 'auto' }}
      >
        <IconRenderer iconName="MenuOpen" />
      </IconButton>
      <Typography variant="h5" sx={{ ml: 3, flexGrow: 1, color: green[900], pointerEvents: 'none' }}>
        Hozenall Cloud
      </Typography>
      <Box sx={{ pointerEvents: 'auto' }}>
        <IconButton onClick={handleMenuClick}>
          {user?.photoURL ? (
            <Avatar src={user.photoURL} alt={user.displayName || "User"} sx={{ width: 32, height: 32 }} />
          ) : (
            <IconRenderer iconName="AccountCircle" />
          )}
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={openClientMenu}
          onClose={handleClose}
          MenuListProps={{
            autoFocus: false,
          }}
        >
          <MenuList style={{ outline: 'none' }}>
            <MenuItem>
              {user?.displayName} さん<br />
              {user?.email}
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <IconRenderer iconName="Logout" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </MenuList>
        </Menu>
      </Box>
    </Toolbar>
  );

  // -------------------------------------------------------------
  // 親 Drawer Menu
  // -------------------------------------------------------------
  const ParentDrawerContent = (
    <Box sx={{ width: '100%' }} role="presentation">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          position: 'sticky', // 縦スクロール時もヘッダーを固定
          top: 0,
          zIndex: 1,
          backgroundColor: 'background.paper',
          pt: 1,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        <Box sx={{ ml: 3, fontSize: 18, fontWeight: 'bold', color: green[900] }}>メニュー</Box>
        <Box>
          <IconButton onClick={handleToggle}>
            <IconRenderer iconName="KeyboardDoubleArrowLeft" />
          </IconButton>
        </Box>
      </Box>

      <List>
        {(() => {
          // 権限があるメニューのみ抽出（Firestore側で sort_no順に並んでいることが前提）
          const accessibleMenus = permissionRolesData.filter(menu =>
            menu && menu.permission && permissionData.includes(menu.permission)
          );

          let lastCategory: string | null = null;

          return accessibleMenus.map((menu, index) => {
            // sort_no の整数部分をカテゴリIDとして使用 (例: 2.1 -> "2")
            const categoryId = Math.floor(menu.sort_no).toString();
            // 表示用の名称を masterData.ts の MENU_CATEGORIES から取得
            const categoryLabel = MENU_CATEGORIES[categoryId];

            // カテゴリが前の項目と異なる場合にのみヘッダーを表示
            const showHeader = categoryLabel && categoryLabel !== lastCategory;
            if (showHeader) {
              lastCategory = categoryLabel;
            }

            // 子メニューデータの正規化
            let children: NavigationMenuType[] = [];
            try {
              if (menu.child) {
                if (Array.isArray(menu.child)) {
                  children = menu.child.filter(c => c !== null);
                } else if (typeof menu.child === 'object') {
                  children = (Object.values(menu.child).filter(c => c !== null) as NavigationMenuType[]);
                }
              }
            } catch (e) {
              console.error("Child data normalization error:", e);
              children = [];
            }

            const hasChildren = children.length > 0;
            const menuId = menu.id || `menu-${index}`;
            const isActive = activeSubmenu?.menuId === menuId;

            return (
              <React.Fragment key={menuId}>
                {showHeader && (
                  <>
                    {index > 0 && <Divider sx={{ my: 1, opacity: 0.6 }} />}
                    <ListSubheader
                      disableSticky // 固定ヘッダー(メニュー)との z-index 重なりを防ぐ
                      sx={{
                        backgroundColor: 'transparent',
                        color: green[800],
                        lineHeight: '36px',
                        mt: index === 0 ? 0 : 1,
                        pointerEvents: 'none', // ヘッダーはクリック不可
                      }}
                    >
                      {categoryLabel}
                    </ListSubheader>
                  </>
                )}
                <ListItem disablePadding>
                  <ListItemButton
                    // 外部 URL は新しいタブで開く。それ以外は画面内遷移
                    {...(isExternalMenuPath(menu.path)
                      ? { component: 'a', href: menu.path, target: '_blank', rel: 'noopener noreferrer' }
                      : { component: menu.path ? RouterLink : 'div', to: menu.path || '#' })}
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                      if (hasChildren && !menu.path) {
                        // 遷移先を持たない親項目はクリックで子メニューを即時展開し、サイドバーは開いたまま維持
                        stopSubmenuSwitchTimer();
                        handleSubMenuOpen(menuId, e, children);
                        return;
                      }
                      // 遷移先を持つ項目は遷移してサイドバーを閉じる
                      handleToggle();
                    }}
                    onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
                      stopCloseTimer();
                      stopSubmenuSwitchTimer();
                      const rect = e.currentTarget.getBoundingClientRect();
                      submenuSwitchTimerRef.current = setTimeout(() => {
                        if (hasChildren) {
                          setActiveSubmenu({
                            menuId,
                            children,
                            top: rect.top
                          });
                        } else {
                          setActiveSubmenu(null);
                        }
                      }, 250);
                    }}
                    sx={{
                      backgroundColor: isActive ? 'action.selected' : 'transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: isActive ? 'action.selected' : 'transparent',
                        transform: 'translateX(5px)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <IconRenderer iconName={menu.icon || 'HelpOutline'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={menu.label || '無題の項目'}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    />
                    {hasChildren && <IconRenderer iconName="ArrowRight" />}
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            );
          });
        })()}
      </List>
    </Box>
  );

  // -------------------------------------------------------------
  // 子メニュー Drawer Content
  // -------------------------------------------------------------
  /* 子メニュー Content */
  const ChildDrawerContent = (
    <Box sx={{ width: '100%', pt: 1 }} role="presentation">

      <List>
        {activeSubmenu?.children.map((childMenu, childIndex) => {
          if (!childMenu || !childMenu.permission || !permissionData.includes(childMenu.permission)) return null;
          return (
            <ListItem key={childMenu.id || `child-${childIndex}`} disablePadding>
              <ListItemButton
                {...(isExternalMenuPath(childMenu.path)
                  ? { component: 'a', href: childMenu.path, target: '_blank', rel: 'noopener noreferrer' }
                  : { component: RouterLink, to: childMenu.path || '#' })}
                onClick={() => {
                  setActiveSubmenu(null);
                  handleToggle();
                }}
                sx={{
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    transform: 'translateX(5px)',
                  }
                }}
              >
                <ListItemIcon>
                  <IconRenderer iconName={childMenu.icon || 'HelpOutline'} />
                </ListItemIcon>
                <ListItemText primary={childMenu.label || '無題の子項目'} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  // -------------------------------------------------------------
  // Return the main component
  // -------------------------------------------------------------
  return (
    <AppBar
      position="fixed"
      elevation={0}
      color="transparent"
      sx={{
        width: '100%',
        left: 0,
        right: 0,
        overflowX: 'hidden',
        pointerEvents: 'none', // 実体を持つ子要素 (Toolbar 内ボタン / Drawer / Backdrop) 側で auto に戻す
      }}
    >
      {HeaderMenuList}

      {/* カスタム Backdrop (z-index: 1200) */}
      <Backdrop
        open={slideOpen}
        onClick={handleToggle}
        sx={{ zIndex: 1200, pointerEvents: 'auto' }}
      />

      {/* 親 Drawer (z-index: 1400) - 最前面 */}
      <Drawer
        variant="persistent"
        open={slideOpen}
        ModalProps={{ disableScrollLock: true }}
        sx={{ pointerEvents: slideOpen ? 'auto' : 'none' }} // 閉じている間は残存 root がクリックを奪わない
        PaperProps={{
          ref: drawerPaperRef,
          onMouseEnter: stopCloseTimer,
          onMouseLeave: startCloseTimer,
          sx: {
            width: 300,
            zIndex: 1400,
            pointerEvents: 'auto', // Paper 自体はクリック有効
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
            boxSizing: 'border-box',
            overflowX: 'hidden', // 内部子要素のはみ出しによる横スクロール抑止
          }
        }}
      >
        {ParentDrawerContent}
      </Drawer>

      {/* 子メニュー (Fade + Paper) - Menu風デザイン */}
      <Fade in={Boolean(activeSubmenu) && slideOpen} timeout={200} key={activeSubmenu?.menuId}>
        <Paper
          onMouseEnter={stopCloseTimer}
          onMouseLeave={startCloseTimer}
          elevation={8}
          sx={{
            position: 'fixed',
            top: activeSubmenu?.top ?? 70, // 親メニュー項目の位置に合わせる
            left: 280, // 親Drawerとわずかに重ねることでホバーの安定性を向上
            height: 'auto',
            pr: 8,
            zIndex: 1500, // 親 Drawer (1400) より上に表示
            borderRadius: '8px',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            bgcolor: 'background.paper',
            boxShadow: '0px 5px 15px rgba(0,0,0,0.1)',
            pointerEvents: 'auto',
          }}
        >
          {ChildDrawerContent}
        </Paper>
      </Fade>
    </AppBar>
  );
}
